import type { PublisherDto } from './publisher';

export interface PublisherManageDto extends PublisherDto {
  isDrmEnabled: boolean;
  defaultPrintQuotaLimit?: number;
  defaultPrintQuotaWindowDays?: number;
  defaultCopyQuotaLimit?: number;
  defaultCopyQuotaWindowDays?: number;
  defaultDownloadQuotaLimit?: number;
  defaultDownloadQuotaWindowDays?: number;
  bookCount: number;
  isDeleted: boolean;
  deletionTime?: string;
  creationTime: string;
  lastModificationTime?: string;
}

export interface PublisherFilterState {
  q?: string;
  isActive?: boolean;
  isDrmEnabled?: boolean;
  createdAfter?: string;     // ISO date
  createdBefore?: string;    // ISO date
  bookCountMin?: number;
  bookCountMax?: number;
  includeDeleted?: boolean;
  sort?: 'name' | 'creationTime' | 'bookCount';
  dir?: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const DEFAULT_PUBLISHER_FILTER_STATE: PublisherFilterState = {
  page: 1,
  pageSize: 25,
  sort: 'name',
  dir: 'asc',
};

export interface PublisherSavedView {
  id: string;
  name: string;
  createdAt: string;
  filters: PublisherFilterState;
}

export interface PublisherSavedViewsStore {
  version: 1;
  views: PublisherSavedView[];
}

export interface CreatePublisherManageDto {
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  isActive: boolean;
  isDrmEnabled: boolean;
  defaultPrintQuotaLimit?: number;
  defaultPrintQuotaWindowDays?: number;
  defaultCopyQuotaLimit?: number;
  defaultCopyQuotaWindowDays?: number;
  defaultDownloadQuotaLimit?: number;
  defaultDownloadQuotaWindowDays?: number;
}

export interface UpdatePublisherManageDto {
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  isActive: boolean;
  isDrmEnabled: boolean;
  defaultPrintQuotaLimit?: number;
  defaultPrintQuotaWindowDays?: number;
  defaultCopyQuotaLimit?: number;
  defaultCopyQuotaWindowDays?: number;
  defaultDownloadQuotaLimit?: number;
  defaultDownloadQuotaWindowDays?: number;
  alsoUpdateBooks: boolean;
  concurrencyStamp: string;
}

export interface BulkSetPublisherActiveDto {
  publisherIds: string[];
  isActive: boolean;
  alsoUpdateBooks: boolean;
}

export interface BulkPublisherQuotaUpdateDto {
  publisherIds: string[];
  isDrmEnabled?: boolean;
  defaultPrintQuotaLimit?: number;
  defaultPrintQuotaWindowDays?: number;
  defaultCopyQuotaLimit?: number;
  defaultCopyQuotaWindowDays?: number;
  defaultDownloadQuotaLimit?: number;
  defaultDownloadQuotaWindowDays?: number;
  alsoUpdateBooks: boolean;
}

export interface BulkDeletePublishersDto {
  publisherIds: string[];
  alsoDeleteBooks: boolean;
}

export interface BulkPublisherItemResult {
  publisherId: string;
  errorMessage: string;
}

export interface BulkPublisherOperationResultDto {
  successCount: number;
  failureCount: number;
  failures: BulkPublisherItemResult[];
}

export interface ManagePublishersListResult {
  items: PublisherManageDto[];
  totalCount: number;
}

/** Format the 6 quota fields as `P:50/30d · C:100/30d · D:10/30d`, or `—` if DRM disabled or any value missing. */
export function formatDrmQuotaSummary(p: PublisherManageDto): string {
  if (!p.isDrmEnabled) return '—';
  const fmt = (limit?: number, days?: number) =>
    limit != null && days != null ? `${limit}/${days}d` : '—';
  return `P:${fmt(p.defaultPrintQuotaLimit, p.defaultPrintQuotaWindowDays)} · ` +
         `C:${fmt(p.defaultCopyQuotaLimit, p.defaultCopyQuotaWindowDays)} · ` +
         `D:${fmt(p.defaultDownloadQuotaLimit, p.defaultDownloadQuotaWindowDays)}`;
}
